import { Injectable } from '@angular/core';
import { SeatInfo, SeatInfoPassengers, SeatNumbers } from '@common/interfaces/servicing-seatmap/seat-info';
import { SeatForServicingSeatmapScreenBySegment } from '@common/interfaces/servicing-seatmap/seatmap-for-seatmap-screen';
import { SeatmapStoreModel } from '@common/interfaces/servicing-seatmap/seatmap-store-model';
import {
  GetOrderResponseData,
  GetSeatmapsResponseDataSeatmapsDecksInner,
  GetSeatmapsResponseDataSeatmapsDecksInnerSeatsRowsInner,
  OrderSeatItem,
  OrderSeatItemSeatSelection,
  Type1,
} from 'src/sdk-servicing';

@Injectable({
  providedIn: 'root',
})
export class ServicingSeatmapService {
  /**
   * 変更があるセグメントがあるか判定
   * @param servicingSeatmap サービシングシートマップStore
   * @param allSegmentInfo 全セグメント情報
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @param pnrInfo PNR情報
   * @returns true:変更があるセグメントあり、false:それ以外
   */
  isSegmentChange(
    servicingSeatmap: SeatmapStoreModel,
    allSegmentInfo: Array<Type1> | undefined,
    selectedSeatInfoList: SeatInfo[] | undefined,
    pnrInfo: GetOrderResponseData | undefined
  ) {
    // 変更があるセグメント取得
    const segments = this.getSegmentChangeList(servicingSeatmap, allSegmentInfo, selectedSeatInfoList, pnrInfo);

    return !!segments && segments.length > 0;
  }

  /**
   * 変更があるセグメントを取得する
   * @param servicingSeatmap サービシングシートマップStore
   * @param allSegmentInfo 全セグメント情報
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @param pnrInfo PNR情報
   * @returns 変更があるセグメント
   */
  getSegmentChangeList(
    servicingSeatmap: SeatmapStoreModel,
    allSegmentInfo: Array<Type1> | undefined,
    selectedSeatInfoList: SeatInfo[] | undefined,
    pnrInfo: GetOrderResponseData | undefined
  ): Type1[] {
    let segments: Type1[] = [];

    if (allSegmentInfo) {
      for (let segment of allSegmentInfo) {
        let seatInfo = selectedSeatInfoList?.find((s) => s.segmentId === segment.id);
        if (seatInfo) {
          for (let passenger of seatInfo.passengerList) {
            // 該当搭乗者の座席に変更がある場合、表示セグメントに追加する
            if (this.isPassengerChange(servicingSeatmap, seatInfo, passenger, pnrInfo)) {
              segments.push(segment);
              break;
            }
          }
        }
      }
    }

    return segments;
  }

  /**
   * 当該搭乗者の座席が変更されたか判定
   * @param servicingSeatmap サービシングシートマップStore
   * @param seatInfo シートマップ情報
   * @param passenger 搭乗者情報
   * @param pnrInfo PNR情報
   * @returns true: 変更あり, false: 変更なし
   */
  isPassengerChange(
    servicingSeatmap: SeatmapStoreModel,
    seatInfo: SeatInfo,
    passenger: SeatInfoPassengers,
    pnrInfo: GetOrderResponseData | undefined
  ): boolean {
    let pnrSeat = !!pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id]
      ? pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id].seatSelection.seatNumbers
      : undefined;
    let pnrCouchSeat = !!pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id]
      ? pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id].seatSelection.couchSeatNumber
      : undefined;
    
    // 座席属性の変更有無フラグ
    const isChangeCharacteristicRequestSsrCode = this.isChangedSeatAttribute(seatInfo);

    if (pnrSeat && pnrSeat.length > 0) {
      // 座席情報の取得
      let seatInformation = this.getSeatInformation(seatInfo.segmentId, pnrSeat[0], servicingSeatmap);

      //  無料席で座席の変更があるかつ、seatNumberList.seatNumber が空欄（無料席取消）の場合、表示セグメントに追加する
      let isFreeSSRSeat = !seatInformation?.isCouchSeat && !seatInformation?.isChargeableAsrSeat;
      if (isFreeSSRSeat && passenger.seatNumberList?.length === 0) {
        return true;
      }
      // SSR席(カウチ席)を変更する場合、trueを返す
      else if (
        passenger.couchSeatNumberList &&
        passenger.couchSeatNumberList.length > 0 &&
        pnrCouchSeat !== passenger.couchSeatNumberList[0].seatNumber
      ) {
        return true;
      }
      // SSR席(有料席)を変更する場合、trueを返す
      else if (
        !pnrCouchSeat &&
        passenger.seatNumberList &&
        passenger.seatNumberList?.length > 0 &&
        pnrSeat[0] !== passenger.seatNumberList[0].seatNumber
      ) {
        return true;
      }
      // SSR登録済み（有料）座席の取消の場合、trueを返す
      else if (passenger.seatNumberList?.length === 0 && passenger.couchSeatNumberList?.length === 0) {
        return true;
      }
      // 座席属性指定の場合、trueを返す
      else if (isChangeCharacteristicRequestSsrCode) {
        // SSR登録済みセグで座席属性指定画面が表示され、属性指定した場合のフロー
        return true;
      }
    }
    // 新規登録の座席の場合、trueを返す
    else if (
      (passenger.seatNumberList && passenger.seatNumberList?.length > 0) ||
      (passenger.couchSeatNumberList &&
        passenger.couchSeatNumberList?.length > 0 &&
        passenger.couchSeatNumberList[0].seatNumber)
    ) {
      return true;
    }
    // 座席属性指定の場合、trueを返す
    else if (isChangeCharacteristicRequestSsrCode) {
      // 座席属性が未登録or登録済み、かつ、座席属性が変更された場合のフロー
      return true;
    }

    // 変更がない場合、falseを返す
    return false;
  }

  /**
   * 選択中の座席情報を取得
   * @param segmentId セグメントID
   * @param seatNumber 座席番号
   * @param servicingSeatmap サービシングシートマップStore
   * @returns 座席情報
   */
  getSeatInformation(segmentId: string, seatNumber: string, servicingSeatmap: SeatmapStoreModel) {
    // 選択中の座席情報取得
    if (!!segmentId && !!seatNumber) {
      let seatInformations = this.getSeatInformations(segmentId, servicingSeatmap);

      if (!!seatInformations && !!seatInformations[seatNumber]) {
        return seatInformations[seatNumber];
      }
    }

    return undefined;
  }

  /**
   * 選択中の座席情報リストを取得
   * @param segmentId セグメントID
   * @param servicingSeatmap サービシングシートマップStore
   * @returns 座席情報
   */
  getSeatInformations(segmentId: string, servicingSeatmap: SeatmapStoreModel) {
    // セグメント別座席情報リストを取得
    const defaultSeatmapInfoBySegmentList = servicingSeatmap.defaultSeatmapInfoBySegmentList || undefined;

    // 選択中の座席情報取得
    if (!!defaultSeatmapInfoBySegmentList && !!segmentId) {
      let seatmapInfoBySegmentList = new Map<string, SeatForServicingSeatmapScreenBySegment>(
        Object.entries(defaultSeatmapInfoBySegmentList)
      );
      let seatInfoBySegment = seatmapInfoBySegmentList?.get(segmentId);

      if (!!seatInfoBySegment?.seatInformations) {
        return seatInfoBySegment.seatInformations;
      }
    }

    return undefined;
  }

  /**
   * 対象セグメントのシートマップ情報を取得
   * @param segmentId セグメントID
   * @param servicingSeatmap サービシングシートマップStore
   * @returns シートマップ情報
   */
  getSeatmaps(segmentId: string, servicingSeatmap: SeatmapStoreModel) {
    // セグメント別座席情報リストを取得
    const defaultSeatmapInfoBySegmentList = servicingSeatmap.defaultSeatmapInfoBySegmentList || undefined;

    // 選択中の座席情報取得
    if (!!defaultSeatmapInfoBySegmentList && !!segmentId) {
      let seatmapInfoBySegmentList = new Map<string, SeatForServicingSeatmapScreenBySegment>(
        Object.entries(defaultSeatmapInfoBySegmentList)
      );
      let seatInfoBySegment = seatmapInfoBySegmentList?.get(segmentId);

      if (!!seatInfoBySegment?.seatmaps) {
        return seatInfoBySegment.seatmaps;
      }
    }

    return undefined;
  }

  /**
   * SSR登録済みの座席属性を取得する。対象PNRの指定セグに含まれる座席属性を返却する。
   * @param segId 取得対象のセグID
   * @param pnr 取得対象のPNR情報
   * @returns 取得できた場合：characteristicRequestSsrCode、取得できなかった場合：空文字
   */
  getRegisteredSeatAttribute(segId: string, pnr?: GetOrderResponseData): string {
    // NOTE：運用上は搭乗者単位で個別に指定可能だが、座席属性は画面では全員同時に同じ値のみ指定可能となる。
    let resultSeatAttribute = '';
    if (!!segId) {
      // PNRに登録された内容を確認して、最初に見つかる値を登録済みの値として採用する。
      const segItems = pnr?.seats?.[segId];
      for (const [k, v] of Object.entries(segItems)) {
        // undefinedか空文字か値が設定される想定
        const segItem: any = v;
        resultSeatAttribute = segItem?.seatSelection?.characteristicRequestSsrCode;
        if (!!resultSeatAttribute) break;
      }
    }
    // undefinedの場合は空文字とする
    return resultSeatAttribute || '';
  }

  /**
   * 選択中座席情報を対象に、座席属性が変更されたかどうか判定
   * @param seatInfo 判定対象の選択中座席情報
   * @returns 変更している場合：true、未変更の場合（判定対象の値が存在しない場合を含む）：false
   */
  isChangedSeatAttribute(seatInfo: SeatInfo): boolean {
    // NOTE：座席属性が指定されたセグは無条件で、座席属性変更した扱いとする（PNR側の差分は不問）
    return !!seatInfo.characteristicRequestSsrCode;
  }

  /**
   * 対象PNR・セグ・搭乗者がカウチSSR登録済みかどうか判定する
   * @param segId 対象セグ
   * @param passengerId 対象搭乗者
   * @param servicingSeatmap サービシングシートマップStore
   * @param pnr 対象のPNR情報
   * @returns true：カウチSSR登録済み、false：カウチSSR未登録（判定できない場合を含む）
   */
  isRegisteredCounch(
    segId: string,
    passengerId: string,
    servicingSeatmap: SeatmapStoreModel,
    pnr?: GetOrderResponseData
  ): boolean {
    let isRegisterd = false;
    if (!!passengerId && !!segId) {
      const registerdSeatNumbers = pnr?.seats?.[segId]?.[passengerId].seatSelection.seatNumbers;
      if (!!registerdSeatNumbers && registerdSeatNumbers.length > 0) {
        const seatInfo = this.getSeatInformation(segId, registerdSeatNumbers[0], servicingSeatmap);
        isRegisterd = !!seatInfo?.isCouchSeat;
      }
    }
    return isRegisterd;
  }

  /**
   * PNRを対象にカウチ席のSSRコードを取得する
   * @param segId 対象セグ
   * @param passengerId 対象搭乗者
   * @param servicingSeatmap サービシングシートマップStore
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @param pnr 対象のPNR情報
   * @returns 取得できた場合：SSRコード、取得できなかった場合：空文字
   */
  getSsrCodeFromRegisteredCouch(
    segId: string,
    passengerId: string,
    servicingSeatmap: SeatmapStoreModel,
    selectedSeatInfoList?: SeatInfo[],
    pnr?: GetOrderResponseData
  ) {
    const seatSelection: OrderSeatItemSeatSelection | undefined = pnr?.seats?.[segId]?.[passengerId]?.seatSelection;
    const couchSeatNumber = seatSelection?.couchSeatNumber;
    if (!!couchSeatNumber) {
      const couchSeatReservedPassengerCount = this.getCouchSeatRegisteredPassengerCount(
        segId,
        couchSeatNumber,
        selectedSeatInfoList,
        pnr
      );
      const couchNumberOfSeats = this.getCouchNumberOfSeats(couchSeatNumber, servicingSeatmap, segId);
      return this.getSsrCodeFromCouch(couchSeatReservedPassengerCount, couchNumberOfSeats);
    }
    return '';
  }

  /**
   * 選択中座席情報リストを対象にカウチ席のSSRコードを取得する
   * @param segId 対象セグ
   * @param seatNumber 対象座席番号
   * @param servicingSeatmap サービシングシートマップStore
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @returns 取得できた場合：SSRコード、取得できなかった場合：空文字
   */
  getSsrCodeFromSelectedCouch(
    segId: string,
    seatNumber: string,
    servicingSeatmap: SeatmapStoreModel,
    selectedSeatInfoList?: SeatInfo[]
  ) {
    const couchSeatNumber = this.getCouchSeatNumber(segId, seatNumber, servicingSeatmap);
    if (!!couchSeatNumber) {
      const couchSeatReservedPassengerCount = this.getCouchSeatSelectedPassengerCount(
        segId,
        couchSeatNumber,
        selectedSeatInfoList
      );
      const couchNumberOfSeats = this.getCouchNumberOfSeats(couchSeatNumber, servicingSeatmap, segId);
      return this.getSsrCodeFromCouch(couchSeatReservedPassengerCount, couchNumberOfSeats);
    }
    return '';
  }

  /**
   * 選択中座席情報リストに含まれる、対象セグ・対象座席番号を元にして、サービス情報登録APIに渡すSSRコードを生成する。
   * SSRコード＝"CO" + "A"(3席カウチの場合) または"B"(4席カウチの場合) + "A"(1名利用)または"B"(2名利用)または"C"(3名利用)または"D"(4名利用)
   * @param couchSeatReservedPassengerCount カウチ座席を選択している搭乗者の数
   * @param couchNumberOfSeats カウチ座席数
   * @returns 取得できた場合：SSRコード、取得できなかった場合：空文字
   */
  getSsrCodeFromCouch(couchSeatReservedPassengerCount: number, couchNumberOfSeats: number) {
    // SSRコード
    let ssrCode = 'CO';

    if (couchNumberOfSeats === 3) {
      ssrCode += 'A';
    } else if (couchNumberOfSeats === 4) {
      ssrCode += 'B';
    } else {
      return '';
    }

    switch (couchSeatReservedPassengerCount) {
      case 1:
        ssrCode += 'A';
        break;
      case 2:
        ssrCode += 'B';
        break;
      case 3:
        ssrCode += 'C';
        break;
      case 4:
        ssrCode += 'D';
        break;
      default:
        return '';
    }
    return ssrCode;
  }

  /**
   * カウチ席数を取得する
   * @param couchSeatNumber 対象カウチ座席番号
   * @param servicingSeatmap サービシングシートマップStore
   * @param segId 対象セグ
   * @returns 取得できた場合：カウチ席数、取得できなかった場合：0
   */
  getCouchNumberOfSeats(couchSeatNumber: string, servicingSeatmap: SeatmapStoreModel, segId: string): number {
    const seatmaps = this.getSeatmaps(segId, servicingSeatmap);
    return (!!seatmaps?.[couchSeatNumber] && (seatmaps?.[couchSeatNumber].couchNumberOfSeats as number)) || 0;
  }

  /**
   * 現在画面上でカウチ座席を選択している搭乗者の数を取得する
   * @param segId 対象セグ
   * @param seatNumber 対象座席番号
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @returns 現在画面上でカウチ座席を選択している搭乗者の数（取得できなかった場合は0）
   */
  getCouchSeatSelectedPassengerCount(segId: string, couchSeatNumber: string, selectedSeatInfoList?: SeatInfo[]) {
    // 現在画面上でカウチ座席を選択している搭乗者の数
    return (
      selectedSeatInfoList
        ?.find((s) => s.segmentId === segId)
        ?.passengerList.filter((p) => p.couchSeatNumberList?.some((c) => c.seatNumber === couchSeatNumber)).length || 0
    );
  }

  /**
   * PNR上でカウチ座席を選択している搭乗者の数を取得する
   * @param segId 対象セグ
   * @param couchSeatNumber 対象カウチ座席番号
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @param pnr 対象のPNR情報
   * @returns PNR上でカウチ座席を選択している搭乗者の数（取得できなかった場合は0）
   */
  getCouchSeatRegisteredPassengerCount(
    segId: string,
    couchSeatNumber: string,
    selectedSeatInfoList?: SeatInfo[],
    pnr?: GetOrderResponseData
  ) {
    // PNRで同じカウチ座席を選択している搭乗者の数
    let countSameCouch = 0;

    // 対象セグの搭乗者IDリスト
    const passengerIds = selectedSeatInfoList
      ?.find((s) => s.segmentId === segId)
      ?.passengerList.map((p) => {
        return p.id;
      });
    passengerIds?.forEach((pid) => {
      const seatSelection: OrderSeatItemSeatSelection | undefined = pnr?.seats?.[segId]?.[pid]?.seatSelection;
      if (seatSelection?.couchSeatNumber === couchSeatNumber) ++countSameCouch;
    });

    return countSameCouch;
  }

  /**
   * 対象セグ・座席番号に対応するカウチ座席番号を取得する
   * @param segId 対象セグ
   * @param seatNumber 対象座席番号
   * @param servicingSeatmap サービシングシートマップStore
   * @returns 取得できた場合：カウチ座席番号、取得できなかった場合：空文字
   */
  getCouchSeatNumber(segId: string, seatNumber: string, servicingSeatmap: SeatmapStoreModel) {
    // 座席情報
    const seatInfo = this.getSeatInformation(segId, seatNumber, servicingSeatmap);

    // カウチ座席番号
    return seatInfo?.couchNumber?.toString() || '';
  }

  /**
   * 座席番号リストから座席番号を取得する
   * @param seatNumberList 座席番号リスト
   * @returns 取得できた場合：座席番号、取得できなかった場合：undefined
   */
  getSeatNumberForSeatNumberList(seatNumberList?: SeatNumbers[]): string | undefined {
    if (!!seatNumberList && seatNumberList.length !== 0) {
      return seatNumberList[0].seatNumber;
    }
    return undefined;
  }
}